import React, {
    forwardRef, useImperativeHandle, useState, useCallback, useMemo,
} from 'react';
import {
    Box, Typography, Divider, TextField, ToggleButton, ToggleButtonGroup,
    Button, Tooltip,
} from '@mui/material';
import { Fullscreen as FullscreenIcon } from '@mui/icons-material';
import MSAModulePage from './MSAModulePage';
import CategoricalGrid from '../CategoricalGrid';
import GuidedEntry from '../GuidedEntry';

// ---------------------------------------------------------------------------
// Type3GRRModule — entry UI for the Type 3 / Expanded GRR study (studyType
// "type3"). Balanced designs only. Two designs:
//
//   nested   : operators nested within parts. Grid = part × operator × trial.
//              count = numParts * numOperators * numTrials.
//   expanded : Part × Operator × one extra CROSSED factor (e.g. Fixture).
//              Grid = part × operator × factorLevel × trial.
//              count = numParts * numOperators * numFactorLevels * numTrials.
//
// Results reuse the shared GRR results layout — MsaResults dispatches type3 to
// GRRResultsSection — so this file authors ONLY the input component. The engine
// rejects unbalanced data; the grid enforces a fully-filled balanced design so
// the count/shape is correct before submit.
// ---------------------------------------------------------------------------

const inputSx = {
    '& .MuiOutlinedInput-root': {
        color: 'rgba(255, 255, 255, 0.9)',
        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' },
        '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
        '&.Mui-focused fieldset': { borderColor: 'rgba(144, 202, 249, 0.7)' },
        '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
    },
    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.6)' },
    '& .MuiInputLabel-root.Mui-focused': { color: 'rgba(144, 202, 249, 0.9)' },
};

const sectionLabelSx = {
    color: 'rgba(255,255,255,0.5)',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
};

// Clamp a config value to [min, 20]; falls back to `fallback` for non-numbers.
function clampConfig(raw, min, fallback) {
    const n = Math.floor(Number(raw));
    if (!Number.isFinite(n)) return fallback;
    return Math.max(min, Math.min(20, n));
}

// Stable composite key for a grid row — lets us preserve entered measurements
// across design/config regeneration (matched by identity, not position).
// IMPORTANT: the key is factor-NAME-independent. It uses the factor LEVEL INDEX
// (factorIdx), not the rendered label (`${factorName}${k}`), so that editing the
// factor name does NOT change any row's identity and therefore does NOT wipe
// already-entered measurements on each keystroke.
function rowKey(design, part, operator, factorIdx, trial) {
    return design === 'expanded'
        ? `${part}|${operator}|f${factorIdx}|${trial}`
        : `${part}|${operator}|${trial}`;
}

// Build the ordered list of grid rows for the current design/config. Order is
// stable and matches the guided-slot order below. Each row carries its label
// fields plus a `_key` (identity), a `factorIdx` (1-based level index, expanded
// only), and an empty `measurement` string.
function generateRows(design, numParts, numOperators, numTrials, factorName, numFactorLevels) {
    const rows = [];
    for (let p = 1; p <= numParts; p++) {
        for (let o = 1; o <= numOperators; o++) {
            const operator = `Op${o}`;
            if (design === 'expanded') {
                for (let k = 1; k <= numFactorLevels; k++) {
                    const factorLevel = `${factorName}${k}`;
                    for (let t = 1; t <= numTrials; t++) {
                        rows.push({
                            _key: rowKey(design, p, operator, k, t),
                            part: p,
                            operator,
                            factorIdx: k,
                            factorLevel,
                            trial: t,
                            measurement: '',
                        });
                    }
                }
            } else {
                for (let t = 1; t <= numTrials; t++) {
                    rows.push({
                        _key: rowKey(design, p, operator, null, t),
                        part: p,
                        operator,
                        factorIdx: null,
                        factorLevel: null,
                        trial: t,
                        measurement: '',
                    });
                }
            }
        }
    }
    return rows;
}

// Human-readable slot label for guided entry (one per grid row). Uses the level
// index (factorIdx) so the label is correct regardless of the factor name.
function slotLabel(row, factorName) {
    return row.factorIdx != null
        ? `Part ${row.part} / ${row.operator} / ${factorName} ${row.factorIdx} / Trial ${row.trial}`
        : `Part ${row.part} / ${row.operator} / Trial ${row.trial}`;
}

function isFilled(v) {
    return v !== null && v !== undefined && String(v).trim() !== '' && Number.isFinite(Number(v));
}

// ---------------------------------------------------------------------------
// Input component (forwardRef, exposes buildInputs()).
// ---------------------------------------------------------------------------

export const Type3GRRInputs = forwardRef(function Type3GRRInputs({ disabled }, ref) {
    const [design, setDesign]                 = useState('nested');
    const [numParts, setNumParts]             = useState(5);
    const [numOperators, setNumOperators]     = useState(2);
    const [numTrials, setNumTrials]           = useState(2);
    const [factorName, setFactorName]         = useState('Fixture');
    const [numFactorLevels, setNumFactorLevels] = useState(2);
    const [tolerance, setTolerance]           = useState('');

    const [rows, setRows] = useState(() => generateRows('nested', 5, 2, 2, 'Fixture', 2));

    // Guided entry state
    const [guidedMode, setGuidedMode]     = useState(false);
    const [guidedSlots, setGuidedSlots]   = useState([]);
    const [guidedValues, setGuidedValues] = useState([]); // parallel to guidedSlots

    // Keep the latest measurement per stable key so a regenerate preserves them.
    const preserveMap = useCallback((existingRows) => {
        const map = new Map();
        existingRows.forEach((r) => { if (r.measurement !== '') map.set(r._key, r.measurement); });
        return map;
    }, []);

    // Regenerate rows on any design/config change, preserving measurements by key.
    const regenerate = useCallback((next) => {
        const d  = next.design ?? design;
        const p  = next.numParts ?? numParts;
        const o  = next.numOperators ?? numOperators;
        const t  = next.numTrials ?? numTrials;
        const fn = next.factorName ?? factorName;
        const fl = next.numFactorLevels ?? numFactorLevels;

        setRows((prev) => {
            const carry = preserveMap(prev);
            const fresh = generateRows(d, p, o, t, fn, fl);
            return fresh.map((r) => (carry.has(r._key) ? { ...r, measurement: carry.get(r._key) } : r));
        });
    }, [design, numParts, numOperators, numTrials, factorName, numFactorLevels, preserveMap]);

    const handleDesignChange = (_e, value) => {
        if (value == null || value === design) return;
        setDesign(value);
        regenerate({ design: value });
    };

    const handleConfigChange = (field, raw) => {
        if (field === 'numParts')        { const v = clampConfig(raw, 2, numParts);        setNumParts(v);        regenerate({ numParts: v }); }
        if (field === 'numOperators')    { const v = clampConfig(raw, 2, numOperators);    setNumOperators(v);    regenerate({ numOperators: v }); }
        if (field === 'numTrials')       { const v = clampConfig(raw, 2, numTrials);       setNumTrials(v);       regenerate({ numTrials: v }); }
        if (field === 'numFactorLevels') { const v = clampConfig(raw, 2, numFactorLevels); setNumFactorLevels(v); regenerate({ numFactorLevels: v }); }
    };

    const handleFactorNameChange = (raw) => {
        // Keep a non-empty factor name; default back to 'Fixture' when cleared.
        const name = raw;
        setFactorName(name);
        const effective = name.trim() === '' ? 'Fixture' : name;
        regenerate({ factorName: effective });
    };

    const handleCellChange = useCallback((rowIndex, key, value) => {
        if (key !== 'measurement') return;
        setRows((prev) => {
            const next = prev.slice();
            next[rowIndex] = { ...next[rowIndex], measurement: value };
            return next;
        });
    }, []);

    // Grid columns depend on design.
    const columns = useMemo(() => {
        const base = [
            { key: 'part',     label: 'Part',     type: 'readonly', align: 'center', width: 64 },
            { key: 'operator', label: 'Operator', type: 'readonly', align: 'center', width: 84 },
        ];
        const factorCol = { key: 'factorLevel', label: factorName || 'Factor', type: 'readonly', align: 'center', width: 96 };
        const tail = [
            { key: 'trial',       label: 'Trial',       type: 'readonly', align: 'center', width: 64 },
            { key: 'measurement', label: 'Measurement', type: 'number',   align: 'right',  width: 120, placeholder: '0.000' },
        ];
        return design === 'expanded' ? [...base, factorCol, ...tail] : [...base, ...tail];
    }, [design, factorName]);

    const expectedCount = design === 'expanded'
        ? numParts * numOperators * numFactorLevels * numTrials
        : numParts * numOperators * numTrials;
    const filledCount = rows.reduce((n, r) => n + (isFilled(r.measurement) ? 1 : 0), 0);

    // -----------------------------------------------------------------------
    // Guided entry — one slot per grid row, carrying rowIndex for write-back.
    // -----------------------------------------------------------------------

    const handleEnterGuided = useCallback(() => {
        const slots = rows.map((r, i) => ({
            label: slotLabel(r, factorName || 'Factor'),
            rowIndex: i,
        }));
        const seeded = rows.map((r) => (isFilled(r.measurement) ? Number(r.measurement) : null));
        setGuidedSlots(slots);
        setGuidedValues(seeded);
        setGuidedMode(true);
    }, [rows, factorName]);

    const handleGuidedSubmitValue = useCallback((val, slotIndex) => {
        setGuidedValues((prev) => {
            const next = prev.slice();
            next[slotIndex] = val;
            return next;
        });
    }, []);

    const handleExitGuided = useCallback(() => {
        setRows((prev) => {
            const next = prev.slice();
            guidedSlots.forEach((slot, i) => {
                const v = guidedValues[i];
                if (v !== null && v !== undefined && Number.isFinite(Number(v))) {
                    const idx = slot.rowIndex;
                    if (next[idx]) next[idx] = { ...next[idx], measurement: String(v) };
                }
            });
            return next;
        });
        setGuidedMode(false);
    }, [guidedSlots, guidedValues]);

    // -----------------------------------------------------------------------
    // buildInputs — assemble + client-validate the module payload.
    // -----------------------------------------------------------------------

    useImperativeHandle(ref, () => ({
        buildInputs() {
            const effectiveFactorName = (factorName || '').trim() === '' ? 'Fixture' : factorName.trim();

            // The factor value is carried on each measurement under the factor NAME
            // key; a name colliding with a fixed measurement field (part/operator/
            // trial/value) would clobber that field. Reject it with a clear message
            // rather than letting a cryptic engine error surface from a 400.
            if (design === 'expanded') {
                const RESERVED = new Set(['part', 'operator', 'trial', 'value']);
                if (RESERVED.has(effectiveFactorName.toLowerCase())) {
                    throw new Error(`Factor Name cannot be "${effectiveFactorName}" — it collides with a reserved measurement field. Use e.g. Fixture, Setup, or Environment.`);
                }
            }

            // Every row must carry a finite measurement (balanced design).
            const remaining = rows.reduce((n, r) => n + (isFilled(r.measurement) ? 0 : 1), 0);
            if (remaining > 0) {
                throw new Error(`Enter a measurement for every row (${remaining} remaining).`);
            }

            const measurements = rows.map((r) => {
                const m = {
                    part:     r.part,
                    operator: r.operator,
                    trial:    r.trial,
                    value:    Number(r.measurement),
                };
                if (design === 'expanded') m[effectiveFactorName] = r.factorLevel;
                return m;
            });

            const payload = {
                measurements,
                design,
                factors: design === 'expanded' ? [effectiveFactorName] : [],
                numParts,
                numOperators,
                numTrials,
            };
            if (design === 'expanded') payload.numFactorLevels = numFactorLevels;

            const tol = tolerance.trim();
            if (tol !== '') {
                const tv = Number(tol);
                if (Number.isFinite(tv)) payload.tolerance = tv;
            }

            return payload;
        },
        // Re-populate design + config + measurement grid from a persisted engine
        // input (history reload). The factor level index is recovered exactly from
        // the persisted level label (`${factorName}${k}`).
        hydrate(input) {
            if (!input || typeof input !== 'object') return;
            const d  = input.design === 'expanded' ? 'expanded' : 'nested';
            const np = Number(input.numParts) || 5;
            const no = Number(input.numOperators) || 2;
            const nt = Number(input.numTrials) || 2;
            const fn = (Array.isArray(input.factors) && input.factors[0]) ? String(input.factors[0]) : 'Fixture';
            const meas = Array.isArray(input.measurements) ? input.measurements : [];

            let nfl = Number(input.numFactorLevels);
            if (!(nfl >= 2)) {
                const distinct = new Set(meas.map((m) => m[fn]).filter((v) => v !== undefined && v !== null));
                nfl = d === 'expanded' ? Math.max(2, distinct.size) : 2;
            }

            setDesign(d);
            setNumParts(np);
            setNumOperators(no);
            setNumTrials(nt);
            setFactorName(fn);
            setNumFactorLevels(nfl);
            setTolerance(input.tolerance !== undefined && input.tolerance !== null ? String(input.tolerance) : '');

            const valueByKey = new Map();
            meas.forEach((m) => {
                let factorIdx = null;
                if (d === 'expanded') {
                    const parsed = parseInt(String(m[fn] ?? '').slice(fn.length), 10);
                    factorIdx = Number.isFinite(parsed) ? parsed : null;
                }
                const key = rowKey(d, m.part, `${m.operator}`, factorIdx, m.trial);
                if (m.value !== undefined && m.value !== null) valueByKey.set(key, String(m.value));
            });
            setRows(generateRows(d, np, no, nt, fn, nfl).map((r) => {
                const v = valueByKey.get(r._key);
                return v !== undefined ? { ...r, measurement: v } : r;
            }));
        },
    }), [rows, design, factorName, numParts, numOperators, numTrials, numFactorLevels, tolerance]);

    // -----------------------------------------------------------------------
    // Guided entry overlay
    // -----------------------------------------------------------------------

    if (guidedMode) {
        return (
            <GuidedEntry
                contextLabel="Type 3 / Expanded GRR — Guided Entry"
                slots={guidedSlots}
                values={guidedValues}
                onSubmitValue={handleGuidedSubmitValue}
                onClose={handleExitGuided}
                appendMode={false}
                contextChips={tolerance !== '' ? [{ label: 'Tolerance', value: tolerance }] : []}
            />
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
            {/* Design selector */}
            <Typography variant="caption" sx={sectionLabelSx}>Design</Typography>
            <ToggleButtonGroup
                exclusive
                size="small"
                value={design}
                onChange={handleDesignChange}
                disabled={disabled}
                sx={{ alignSelf: 'flex-start', flexWrap: 'wrap' }}
            >
                {[
                    { v: 'nested',   label: 'Nested' },
                    { v: 'expanded', label: 'Expanded' },
                ].map(({ v, label }) => (
                    <ToggleButton
                        key={v}
                        value={v}
                        sx={{
                            px: 2, py: 0.5,
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            color: 'rgba(255,255,255,0.6)',
                            borderColor: 'rgba(255,255,255,0.15)',
                            '&.Mui-selected': {
                                color: '#90caf9',
                                backgroundColor: 'rgba(144,202,249,0.14)',
                                borderColor: 'rgba(144,202,249,0.4)',
                                '&:hover': { backgroundColor: 'rgba(144,202,249,0.2)' },
                            },
                        }}
                    >
                        {label}
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', mt: -1, lineHeight: 1.5 }}>
                {design === 'nested'
                    ? 'Operators nested within parts — part × operator × trial.'
                    : `Part × Operator × ${factorName || 'Factor'} (crossed) × trial.`}
            </Typography>

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

            {/* Study design config */}
            <Typography variant="caption" sx={sectionLabelSx}>Study Design</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
                <TextField
                    label="Parts"
                    type="number"
                    size="small"
                    sx={inputSx}
                    value={numParts}
                    onChange={(e) => handleConfigChange('numParts', e.target.value)}
                    disabled={disabled}
                    inputProps={{ min: 2, max: 20, step: 1, inputMode: 'numeric' }}
                />
                <TextField
                    label="Operators"
                    type="number"
                    size="small"
                    sx={inputSx}
                    value={numOperators}
                    onChange={(e) => handleConfigChange('numOperators', e.target.value)}
                    disabled={disabled}
                    inputProps={{ min: 2, max: 20, step: 1, inputMode: 'numeric' }}
                />
                <TextField
                    label="Trials"
                    type="number"
                    size="small"
                    sx={inputSx}
                    value={numTrials}
                    onChange={(e) => handleConfigChange('numTrials', e.target.value)}
                    disabled={disabled}
                    inputProps={{ min: 2, max: 20, step: 1, inputMode: 'numeric' }}
                />
            </Box>

            {/* Expanded-only factor config */}
            {design === 'expanded' && (
                <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 1 }}>
                    <TextField
                        label="Factor Name"
                        size="small"
                        sx={inputSx}
                        value={factorName}
                        onChange={(e) => handleFactorNameChange(e.target.value)}
                        disabled={disabled}
                        placeholder="e.g. Fixture"
                        inputProps={{ maxLength: 40 }}
                    />
                    <TextField
                        label="Levels"
                        type="number"
                        size="small"
                        sx={inputSx}
                        value={numFactorLevels}
                        onChange={(e) => handleConfigChange('numFactorLevels', e.target.value)}
                        disabled={disabled}
                        inputProps={{ min: 2, max: 20, step: 1, inputMode: 'numeric' }}
                    />
                </Box>
            )}

            {/* Count summary + guided entry */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                    {design === 'expanded'
                        ? `${numParts} × ${numOperators} × ${numFactorLevels} × ${numTrials} = ${expectedCount} measurements`
                        : `${numParts} × ${numOperators} × ${numTrials} = ${expectedCount} measurements`}
                    {filledCount > 0 && ` (${filledCount} entered)`}
                </Typography>
                <Tooltip title="Switch to guided fullscreen entry — walks every row in order (keyboard wedge / mobile keypad)">
                    <span>
                        <Button
                            size="small"
                            startIcon={<FullscreenIcon />}
                            onClick={handleEnterGuided}
                            disabled={disabled}
                            sx={{ fontSize: '0.7rem', color: 'rgba(144,202,249,0.7)', py: 0.25, px: 1 }}
                        >
                            Guided Entry
                        </Button>
                    </span>
                </Tooltip>
            </Box>

            {/* Tolerance (optional) */}
            <TextField
                label="Tolerance (USL − LSL)"
                size="small"
                fullWidth
                sx={inputSx}
                value={tolerance}
                onChange={(e) => setTolerance(e.target.value)}
                disabled={disabled}
                placeholder="Optional — enables %Tolerance columns"
                inputProps={{ inputMode: 'decimal' }}
            />

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

            {/* Helper text */}
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', lineHeight: 1.5 }}>
                Part, Operator{design === 'expanded' ? `, ${factorName || 'Factor'}` : ''}, and Trial columns are
                auto-populated. Enter values in the{' '}
                <strong style={{ color: 'rgba(144,202,249,0.8)' }}>Measurement</strong> column only — all rows are
                required (balanced design).
            </Typography>

            {/* Measurement grid — scrolls inside its own container */}
            <CategoricalGrid
                columns={columns}
                rows={rows}
                onCellChange={handleCellChange}
                disabled={disabled}
                maxHeight={420}
            />
        </Box>
    );
});

// ---------------------------------------------------------------------------
// Page — wires the input component into the shared MSAModulePage chrome.
// ---------------------------------------------------------------------------

export default function Type3GRRModule({ onBack }) {
    return (
        <MSAModulePage
            studyType="type3"
            title="Type 3 / Expanded GRR"
            subtitle="Nested or Multi-Factor"
            onBack={onBack}
            descriptionPlaceholder="e.g. Shaft OD gage #772, nested operator re-study"
            helperText="Balanced designs only. Nested = operators within parts; Expanded = adds one crossed factor (e.g. Fixture)."
        >
            {({ inputRef, disabled }) => <Type3GRRInputs ref={inputRef} disabled={disabled} />}
        </MSAModulePage>
    );
}
